import type { Budget, versionInfo } from "../types/budget/budget";
import type { ServiceData } from "../types/budget/service-item";
import {
  applyTransferServiceSupplier,
  type TransferServiceSupplierPayload,
} from "../contexts/BudgetContext/reducer-funcion";
import { calculateAndMapService } from "../contexts/BudgetContext/calc-methods";
import { getPhaseValuesFromService } from "./budget-service-writes";

/**
 * Fatias do orçamento de que a projeção precisa. São exatamente as coleções que
 * `applyTransferServiceSupplier` lê/escreve e as que `calculateAndMapService`
 * consulta (`baseValues` para o perfil fiscal da categoria, `budgetServices`
 * para o rateio por número de serviços).
 */
export type TransferProjectionSlices = Pick<
  Budget,
  | "budgetServices"
  | "baseValues"
  | "mainAppliedTaxes"
  | "supplierList"
  | "addedItemTypeList"
>;

export type TransferValueProjection = {
  /** Total em BRL do serviço na etapa corrente, hoje. */
  current: number;
  /** Total em BRL que o serviço passaria a ter no fornecedor de destino. */
  projected: number;
  /** `projected - current`, para a UI não repetir a conta. */
  delta: number;
  /** `true` quando a diferença é maior que meio centavo. */
  changes: boolean;
};

/** Abaixo disto a diferença é ruído de ponto flutuante, não mudança real. */
const EPSILON = 0.005;

function readTotalBRL(
  service: ServiceData | undefined,
  info: versionInfo
): number {
  if (!service) return 0;
  const values = getPhaseValuesFromService(service, info);
  return Number(values?.totals?.totalInBRL) || 0;
}

/**
 * Quanto o serviço passará a valer depois de transferido, ANTES de qualquer
 * dispatch.
 *
 * Taxas, impostos e câmbio pertencem à CATEGORIA (`baseValues`), não ao serviço
 * — então entrar numa categoria que já existe no fornecedor de destino muda o
 * valor da linha. O usuário precisa ver esse número no modal de confirmação, e
 * não descobrir depois de confirmar.
 *
 * A projeção roda a MESMA função que o reducer usa para transferir de verdade
 * (`applyTransferServiceSupplier`) sobre uma cópia das fatias relevantes, e só
 * então recalcula o serviço. Assim a previsão não pode divergir do efeito real:
 * se a regra de clone do perfil fiscal mudar, esta função acompanha sozinha.
 *
 * Devolve `null` quando não há o que projetar (serviço ausente, payload
 * inválido, ou a transferência não alteraria nada).
 */
export function projectTransferredServiceValue(
  slices: TransferProjectionSlices,
  payload: TransferServiceSupplierPayload,
  info: versionInfo
): TransferValueProjection | null {
  if (!slices?.budgetServices?.length || !payload?.serviceId) return null;

  const budgetLike = {
    budgetServices: slices.budgetServices ?? [],
    baseValues: slices.baseValues ?? [],
    mainAppliedTaxes: slices.mainAppliedTaxes ?? [],
    supplierList: slices.supplierList ?? [],
    addedItemTypeList: slices.addedItemTypeList ?? [],
  } as unknown as Budget;

  const before = budgetLike.budgetServices.find(
    (el) => el._id === payload.serviceId
  );
  if (!before) return null;

  const after = applyTransferServiceSupplier(budgetLike, payload);
  // Payload inválido: o reducer devolve o mesmo objeto por referência.
  if (after === budgetLike) return null;

  const moved = after.budgetServices.find((el) => el._id === payload.serviceId);
  if (!moved) return null;

  // O valor de hoje também passa pelo recálculo, e não é lido direto de
  // `totals`: comparar um total possivelmente defasado (gravado antes da última
  // edição de taxa) com um recalculado acusaria mudanças que a transferência
  // não causou.
  const current = readTotalBRL(
    calculateAndMapService(before, budgetLike, info) as ServiceData,
    info
  );
  const projected = readTotalBRL(
    calculateAndMapService(moved, after, info) as ServiceData,
    info
  );

  const delta = projected - current;

  return {
    current,
    projected,
    delta,
    changes: Math.abs(delta) >= EPSILON,
  };
}
